/**
 * Human-readable labels for player error codes.
 *
 * Two enums are involved when a playback error reaches the Cast receiver:
 *
 * - **CAF `DetailedErrorCode`** — the high-level error category reported by the
 *   Cast Application Framework on the `ERROR` event (e.g. `100 MEDIA_UNKNOWN`,
 *   `905 LOAD_FAILED`).
 * - **Shaka error code** — the low-level cause from the embedded Shaka player,
 *   surfaced on `error.shakaErrorCode` (e.g. `6007 LICENSE_REQUEST_FAILED`).
 *
 * These maps turn the raw numbers into readable labels so observability tools
 * (Mux, Datadog, Firebase) carry the *meaning* of the failure, not just a code.
 * Shaka codes that BP actually observes are covered; see the Mux error
 * inventory for the source list.
 *
 * > Shaka codes should be confirmed against the Shaka version embedded in the
 * > CAF build — they are stable across recent releases but not guaranteed.
 */

/** CAF `DetailedErrorCode` → label (mirrors the SDK enum). */
export const DETAILED_ERROR_LABELS: Record<number, string> = {
  100: 'MEDIA_UNKNOWN',
  101: 'MEDIA_ABORTED',
  102: 'MEDIA_DECODE',
  103: 'MEDIA_NETWORK',
  104: 'MEDIA_SRC_NOT_SUPPORTED',
  110: 'SOURCE_BUFFER_FAILURE',
  200: 'MEDIAKEYS_UNKNOWN',
  201: 'MEDIAKEYS_NETWORK',
  202: 'MEDIAKEYS_UNSUPPORTED',
  203: 'MEDIAKEYS_WEBCRYPTO',
  300: 'NETWORK_UNKNOWN',
  301: 'SEGMENT_NETWORK',
  311: 'HLS_NETWORK_MASTER_PLAYLIST',
  312: 'HLS_NETWORK_PLAYLIST',
  313: 'HLS_NETWORK_NO_KEY_RESPONSE',
  314: 'HLS_NETWORK_KEY_LOAD',
  315: 'HLS_NETWORK_INVALID_SEGMENT',
  316: 'HLS_SEGMENT_PARSING',
  321: 'DASH_NETWORK',
  322: 'DASH_NO_INIT',
  331: 'SMOOTH_NETWORK',
  332: 'SMOOTH_NO_MEDIA_DATA',
  400: 'MANIFEST_UNKNOWN',
  411: 'HLS_MANIFEST_MASTER',
  412: 'HLS_MANIFEST_PLAYLIST',
  420: 'DASH_MANIFEST_UNKNOWN',
  421: 'DASH_MANIFEST_NO_PERIODS',
  422: 'DASH_MANIFEST_NO_MIMETYPE',
  423: 'DASH_INVALID_SEGMENT_INFO',
  431: 'SMOOTH_MANIFEST',
  500: 'SEGMENT_UNKNOWN',
  600: 'TEXT_UNKNOWN',
  900: 'APP',
  901: 'BREAK_CLIP_LOADING_ERROR',
  902: 'BREAK_SEEK_INTERCEPTOR_ERROR',
  903: 'IMAGE_ERROR',
  904: 'LOAD_INTERRUPTED',
  905: 'LOAD_FAILED',
  906: 'MEDIA_ERROR_MESSAGE',
  999: 'GENERIC',
};

/** Shaka error code → label (subset observed in the Mux error inventory). */
export const SHAKA_ERROR_LABELS: Record<number, string> = {
  1001: 'BAD_HTTP_STATUS',
  1002: 'HTTP_ERROR',
  1003: 'TIMEOUT',
  6001: 'REQUESTED_KEY_SYSTEM_CONFIG_UNAVAILABLE',
  6002: 'FAILED_TO_CREATE_CDM',
  6003: 'FAILED_TO_ATTACH_TO_VIDEO',
  6004: 'INVALID_SERVER_CERTIFICATE',
  6005: 'FAILED_TO_CREATE_SESSION',
  6006: 'FAILED_TO_GENERATE_LICENSE_REQUEST',
  6007: 'LICENSE_REQUEST_FAILED',
  6008: 'LICENSE_RESPONSE_REJECTED',
  7000: 'LOAD_INTERRUPTED',
};

/** Returns the CAF label for a detailed error code, or a `code N` fallback. */
export function detailedErrorLabel(code: number | undefined): string {
  if (code == null) return 'unknown';
  return DETAILED_ERROR_LABELS[code] ?? `code ${code}`;
}

/** Returns the Shaka label for an error code, or a `shaka N` fallback. */
export function shakaErrorLabel(code: number | undefined): string {
  if (code == null) return '';
  return SHAKA_ERROR_LABELS[code] ?? `shaka ${code}`;
}

interface PlayerErrorParts {
  detailedErrorCode?: number | undefined;
  shakaErrorCode?: number | undefined;
  mediaId?: string | undefined;
  reason?: string | undefined;
}

/**
 * Builds a single human-readable message from the parts of a player error.
 *
 * Example: `LOAD_FAILED (905) · LICENSE_REQUEST_FAILED (shaka 6007) · media=abc123`
 *
 * This is what makes errors actionable in Mux — the CAF `ErrorEvent` rarely
 * carries `error.message`, which is why the dashboard shows "Error did not
 * include a message" for the bulk of the volume.
 */
export function buildPlayerErrorMessage(parts: PlayerErrorParts): string {
  const { detailedErrorCode, shakaErrorCode, mediaId, reason } = parts;

  const segments: string[] = [];

  if (detailedErrorCode != null) {
    segments.push(`${detailedErrorLabel(detailedErrorCode)} (${detailedErrorCode})`);
  }

  if (shakaErrorCode != null) {
    segments.push(`${shakaErrorLabel(shakaErrorCode)} (shaka ${shakaErrorCode})`);
  }

  if (reason) {
    segments.push(reason);
  }

  if (mediaId) {
    segments.push(`media=${mediaId}`);
  }

  return segments.length > 0 ? segments.join(' · ') : 'Unknown player error';
}
